import { useCustomer, useDeleteCustomer } from "common";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { Button, Spinner, XStack, YStack, Text, Paragraph } from "tamagui";

import { CustomerInfoCard } from "./components/CustomerInfoCard";
import ConfirmDialog from "../../components/ConfirmDialog";

export const CustomerView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { customer, error, loading } = useCustomer(id as string);
  const { remove } = useDeleteCustomer();

  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleCustomerDeleteSubmit = async () => {
    try {
      if (id) {
        await remove(id);
        navigate("/customers");
      }
    } catch (error) {
      console.error("Error while deleting customer", error);
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <YStack flex={1} justifyContent="center" alignItems="center" padding="$4">
        <Spinner size="large" />
        <Text marginTop="$2">{t("common.loading", "Loading...")}</Text>
      </YStack>
    );
  }

  if (error) {
    return (
      <YStack padding="$4">
        <Text color="$red10" fontSize="$5">
          {t("common.error", "Error loading customers.")}
        </Text>
        {error && error.message && <Paragraph>{error.message}</Paragraph>}
      </YStack>
    );
  }

  return (
    <YStack flex={1} padding="$4" space="$4" backgroundColor="$backgroundSoft">
      <XStack
        justifyContent="space-between"
        alignItems="center"
        marginBottom="$2"
      >
        <Text fontSize="$8" fontWeight="bold" color="$color">
          {t("customerView.title", "Customer View")}
        </Text>
        <XStack justifyContent="space-between" alignItems="center" space="$4">
          <Button
            color="red"
            size="$4"
            onPress={() => setDeleteDialogOpen(true)}
          >
            Delete
          </Button>
          <Button
            theme="active"
            size="$4"
            onPress={() => navigate("/customers")}
          >
            Go Back
          </Button>
        </XStack>
      </XStack>
      <YStack space="$4" flex={1} paddingTop="$4">
        {customer && <CustomerInfoCard customer={customer} />}
      </YStack>

      <ConfirmDialog
        open={isDeleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={() => handleCustomerDeleteSubmit()}
        title={t("customerView.delete.confirm", "Delete Customer?")}
        description={t(
          "customerView.delete.description",
          "Are you sure you want to delete this Customer? This action cannot be undone.",
        )}
      ></ConfirmDialog>
    </YStack>
  );
};

export default CustomerView;
